import React, { useState, useEffect } from 'react';
import {
    BookmarkSimple,
    X,
    ArrowUUpLeft,
    CaretDown,
    ArrowSquareOut,
    WarningCircle,
    SpinnerGap,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ContractText, ContractBadge } from '@/components/system/ContractUI';
import { request } from '@/lib/api';
import { SEMANTIC_COLORS, VERDICT_PILL } from '@/config/ui-invariants';
import { AGENT_CONFIG, INTENT_TO_BUTTON, SHARED_UI_LABELS } from '@/config/task-display-config';

const SI_CONFIG = AGENT_CONFIG.sector_intelligence || {};
const SI_ACTIONS = SI_CONFIG.actions || [];
const ACCEPT_ACTION = SI_ACTIONS.find(a => a.id === 'accept') || {};
const REJECT_ACTION = SI_ACTIONS.find(a => a.id === 'reject') || {};
const OVERRIDE_ACTION = SI_ACTIONS.find(a => a.id === 'override') || {};

// EVERY BUTTON ON THIS CARD NAMES THE ACTION THAT WILL HAPPEN, and the minting phrase has ONE
// home so the primary and the override cannot drift apart (CEO ruling 2026-09-09, P-2129 and
// its 9-Sep correction).
//
// THE OVERRIDE IS "TAKE THE OPPOSITE OF THE CHECKER", AND ITS TWO DIRECTIONS DO OPPOSITE
// THINGS — which is why its label is computed from the recommendation on screen rather than
// fixed. It previously read "Override to Reject" / "Override to Save", naming the button
// instead of the consequence:
//   * checker recommended SAVE + override  -> NOT saved, NOTHING minted (POST .../reject)
//   * checker recommended DISCARD + override -> saved, and the mint RUNS (POST .../override)
// Both directions are proven on the real routes by
// backend/tests/test_article_override_both_directions_mint.py, which plants a mint inside the
// reject path and catches it.
const MINT_PHRASE = 'mint LinkedIn ideas';
const PRIMARY_SAVE_LABEL = `Save to KB and ${MINT_PHRASE}`;
const OVERRIDE_LABEL_WHEN_RECOMMENDED_SAVE = 'Override: do not save';
const OVERRIDE_LABEL_WHEN_RECOMMENDED_DISCARD = `Override: save and ${MINT_PHRASE}`;
import { RecommendationPill } from '@/components/shared/RecommendationPill';

/**
 * ArticleReviewDetail - Renders article_review work items in the inbox detail panel.
 * Authority: docs/agents/article/ARTICLE_AGENT_SPEC.md v2.0 (Phase 4)
 *
 * Layout:
 *   Source Attribution -> Signal + Recommendation badges
 *   -> Confidence Statement (color-coded by signal strength)
 *   -> Summary -> Analysis -> Counter-Argument (3-paragraph callout)
 *   -> Downstream Utility -> Source URL -> Gates
 *   -> Calibration selector -> Action Buttons
 */

// Signal strength → VERDICT_PILL mapping (same as linkedInSignalPill in task-display-config)
const SIGNAL_TO_VERDICT = {
    strong_signal: { label: SHARED_UI_LABELS.strongSignal, verdict: 'strong' },
    signal:        { label: 'Signal',                      verdict: 'moderate' },
    noise:         { label: 'Noise',                       verdict: 'weak' },
};

// Task 6: Confidence statement color spectrum based on signal strength
const CONFIDENCE_COLORS = {
    strong_signal: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    signal: 'bg-amber-50 border-amber-200 text-amber-900',
    noise: 'bg-red-50 border-red-200 text-red-800',
};

// PKG-024 heal: PROVENANCE_STYLES derives from SEMANTIC_COLORS.provenance; `contrary` type added (2.3.PRODUCT_CONTRACT_VIOLATION resolved)
const PROVENANCE_STYLES = Object.fromEntries(
    Object.entries(SEMANTIC_COLORS.provenance).map(([key, intent]) => [
        key, `${intent.bg} ${intent.text} ${intent.border}`
    ])
);

const UTILITY_LABELS = {
    content: 'Content',
    advisory: 'Advisory',
    pitch_review: 'Pitch Review',
    competitive: 'Competitive',
    thesis: 'Thesis',
};

// Task 5: Business-facing calibration labels
const CALIBRATION_LABELS = {
    agreed: 'Agent got it right',
    rated_higher: 'I rate higher',
    rated_lower: 'I rate lower',
    would_have_saved: 'Would have saved',
};

// Frontend safety net: strip residual 24-char hex ObjectIds that backend may not have resolved
const OBJECTID_PATTERN = /\b[0-9a-f]{24}\b/gi;
function stripObjectIds(text) {
    if (!text) return text;
    return text.replace(OBJECTID_PATTERN, '[KB reference]');
}

// Task 4: Parse [tag:tag-name] markers and render as colored inline chips
const TAG_PATTERN = /\[tag:([^\]]+)\]/g;

// PKG-024 heal: TAG_PROVENANCE_COLORS shares PROVENANCE_STYLES source; `contrary` type included (2.3.PRODUCT_CONTRACT_VIOLATION resolved)
const TAG_PROVENANCE_COLORS = PROVENANCE_STYLES;

function renderTextWithTags(text, tagProvenanceMap) {
    if (!text || !TAG_PATTERN.test(text)) return text;
    // Reset lastIndex after test
    TAG_PATTERN.lastIndex = 0;

    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = TAG_PATTERN.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        const tagName = match[1];
        const provenance = tagProvenanceMap[tagName] || 'novel';
        const colorClass = TAG_PROVENANCE_COLORS[provenance] || TAG_PROVENANCE_COLORS.novel;
        parts.push(
            <span
                key={`tag-${match.index}`}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border mx-0.5 align-baseline ${colorClass}`}
            >
                {tagName}
            </span>
        );
        lastIndex = TAG_PATTERN.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts;
}

export function ArticleReviewDetail({ item, onArticleAction }) {
    const [rejectionReasons, setRejectionReasons] = useState([]);
    const [overrideReasons, setOverrideReasons] = useState([]);
    const [calibrationValues, setCalibrationValues] = useState([]);
    const [selectedCalibration, setSelectedCalibration] = useState(null);
    const [busy, setBusy] = useState(false);

    const payload = item.payload || {};
    const signalEntry = SIGNAL_TO_VERDICT[payload.signal_strength] || SIGNAL_TO_VERDICT.noise;
    const signalVerdict = VERDICT_PILL[signalEntry.verdict] || VERDICT_PILL.weak;
    const confidenceColor = CONFIDENCE_COLORS[payload.signal_strength] || CONFIDENCE_COLORS.noise;

    const tagProvenanceMap = {};
    const hasInlineTags = (payload.accrezeo_analysis || '').includes('[tag:');

    // Load rejection reasons, override reasons, and calibration values from backend
    useEffect(() => {
        request('/api/articles/rejection-reasons')
            .then(data => setRejectionReasons(data.reasons || []))
            .catch(() => setRejectionReasons([
                'Analysis too shallow',
                'Not relevant to Accrezeo',
                'Weak contrarian angle',
                'Duplicate or overlapping',
                'Low signal — PR or press release',
            ]));

        request('/api/articles/override-reasons')
            .then(data => setOverrideReasons(data.reasons || []))
            .catch(() => setOverrideReasons([
                'Cross-sector relevance',
                'Advisory or mentorship value',
                'Emerging thesis signal',
                'Contains rare data or metrics',
            ]));

        request('/api/articles/calibration-values')
            .then(data => setCalibrationValues(data.values || []))
            .catch(() => setCalibrationValues([
                'agreed', 'rated_higher', 'rated_lower', 'would_have_saved',
            ]));
    }, []);

    const handleAction = async (action, reason) => {
        if (onArticleAction) {
            setBusy(true);
            try {
                await onArticleAction(
                    payload.article_id || item.id,
                    action,
                    reason,
                    selectedCalibration,
                );
            } finally {
                setBusy(false);
            }
        }
    };

    // Filter calibration options based on action context
    const visibleCalibrations = calibrationValues.filter(v => {
        // "would_have_saved" only relevant when agent recommended discard
        if (v === 'would_have_saved' && payload.recommendation === 'save') return false;
        return true;
    });

    return (
        <div className="space-y-6">
            {/* Source Attribution — "Received via: Channel / Publication" format (Task 1) */}
            {(payload.source_channel || payload.source_publication) && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="text-slate-400 font-medium">Received via:</span>
                    {payload.source_channel && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 font-medium border border-slate-200">
                            {payload.source_channel}
                        </span>
                    )}
                    {payload.source_channel && payload.source_publication && (
                        <span className="text-slate-300">/</span>
                    )}
                    {payload.source_publication && (
                        <span className="font-semibold text-slate-700">
                            {payload.source_publication}
                        </span>
                    )}
                </div>
            )}

            {/* Recommendation Badge + Signal Strength — VERDICT_PILL tokens */}
            <div className="flex items-center gap-3 flex-wrap">
                <span style={{
                    ...VERDICT_PILL.pill,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: signalVerdict.background,
                    color: signalVerdict.text,
                    border: `1px solid ${signalVerdict.border}`,
                    fontWeight: '700',
                }}>
                    <span style={{
                        width: signalVerdict.dotSize,
                        height: signalVerdict.dotSize,
                        borderRadius: '50%',
                        background: signalVerdict.dot,
                        flexShrink: 0,
                    }} />
                    {signalEntry.label}
                </span>
                {payload.recommendation && (
                    <RecommendationPill type={payload.recommendation} />
                )}
            </div>

            {/* Confidence Statement — color-coded by signal strength (Task 6) */}
            {payload.confidence_statement && (
                <div className={`border rounded-lg p-4 ${confidenceColor}`}>
                    <ContractText variant="body" className="italic">
                        {payload.confidence_statement}
                    </ContractText>
                </div>
            )}

            {/* Neutral Summary */}
            {payload.neutral_summary && (
                <div>
                    <ContractText variant="sectionLabel">Summary</ContractText>
                    <ContractText variant="body" className="mt-2 leading-relaxed text-slate-700">
                        {payload.neutral_summary}
                    </ContractText>
                </div>
            )}

            {/* Accrezeo Analysis — with inline belief tag chips (Task 4) */}
            {payload.accrezeo_analysis && (
                <div>
                    <ContractText variant="sectionLabel">Accrezeo Analysis</ContractText>
                    <div className="mt-2 prose prose-sm max-w-none text-slate-800 leading-relaxed">
                        {payload.accrezeo_analysis.split('\n\n').map((paragraph, i) => (
                            <p key={i} className="mb-3 text-base leading-relaxed">
                                {renderTextWithTags(paragraph, tagProvenanceMap)}
                            </p>
                        ))}
                    </div>
                </div>
            )}

            {/* Counter-Argument (Warning Callout — paragraphs split for readability) */}
            {payload.counter_argument && (
                <div>
                    <ContractText variant="sectionLabel">Counter-Argument</ContractText>
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                        <WarningCircle weight="fill" className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-3">
                            {payload.counter_argument.split('\n\n').map((paragraph, i) => (
                                <ContractText key={i} variant="body" className="text-amber-900 leading-relaxed">
                                    {paragraph}
                                </ContractText>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Downstream Utility */}
            {payload.downstream_utility && payload.downstream_utility.length > 0 && (
                <div>
                    <ContractText variant="sectionLabel">Use Cases</ContractText>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {payload.downstream_utility.map((tag, i) => (
                            <ContractBadge key={i} intent="info">
                                {UTILITY_LABELS[tag] || tag}
                            </ContractBadge>
                        ))}
                    </div>
                </div>
            )}

            {/* Cross-Sector Parallels — ObjectIds replaced by backend; frontend safety net strips residuals */}
            {payload.cross_sector_parallels && payload.cross_sector_parallels !== 'No structural parallels found in current KB.' && (
                <div>
                    <ContractText variant="sectionLabel">Cross-Sector Parallels</ContractText>
                    <ContractText variant="body" className="mt-2 text-slate-700 leading-relaxed">
                        {stripObjectIds(payload.cross_sector_parallels)}
                    </ContractText>
                </div>
            )}

            {/* Source URL */}
            {payload.source_url && (
                <div>
                    <a
                        href={payload.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-base font-medium text-brand-700 hover:text-brand-900 transition-colors"
                    >
                        <ArrowSquareOut weight="bold" className="w-4 h-4" />
                        Read Original Article
                    </a>
                </div>
            )}

            {/* Gates Passed */}
            {payload.gates_passed && payload.gates_passed.length > 0 && (
                <details className="text-sm text-slate-500 cursor-pointer">
                    <summary className="font-medium hover:text-slate-700">
                        Save Gates ({payload.gates_passed.filter(g => typeof g === 'string' ? true : g.passed).length}/{payload.gates_passed.length} passed)
                    </summary>
                    <div className="mt-2 space-y-1 pl-2">
                        {payload.gates_passed.map((gate, i) => {
                            const isString = typeof gate === 'string';
                            const passed = isString ? true : gate.passed;
                            const label = (isString ? gate : gate.gate || '').replace(/_/g, ' ');
                            return (
                                <div key={i} className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${passed ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                    <span className="text-sm">{label}</span>
                                </div>
                            );
                        })}
                    </div>
                </details>
            )}

            {/* CEO Calibration Selector (Task 5) */}
            {visibleCalibrations.length > 0 && (
                <div>
                    <ContractText variant="sectionLabel">Your Assessment</ContractText>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {visibleCalibrations.map((value) => (
                            <button
                                key={value}
                                type="button"
                                className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors min-h-[44px] ${
                                    selectedCalibration === value
                                        ? 'bg-brand-900 text-white border-brand-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-brand-900 hover:text-brand-900'
                                }`}
                                onClick={() => setSelectedCalibration(
                                    selectedCalibration === value ? null : value
                                )}
                            >
                                {CALIBRATION_LABELS[value] || value}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Action Buttons — Recommendation-Driven */}
            <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center gap-3 flex-wrap">
                    {payload.recommendation === 'save' ? (
                        <>
                            {/* Direct Action: Save to KB — intent: primary.
                                THE LABEL SAYS WHAT PRESSING IT STARTS (CEO ruling 2026-09-09, P-2129).
                                It read "Save to KB", and nothing on this card told the CEO that
                                approving an article is what mints LinkedIn ideas from it — so he was
                                approving idea-minting without being told that is what he was doing.
                                THE MINT RUNS IN THIS CLICK, SYNCHRONOUSLY — corrected before shipping,
                                after a first draft of this comment claimed the 07:00 sweep did it.
                                `save_article` inline-awaits `_dispatch_linkedin_trigger`
                                (`article_actions.py:463`), which inline-awaits `evaluate_opportunity`
                                in the SAME request; that is deliberate and load-bearing — it replaced
                                an `ensure_future` fire-and-forget that lost 11 accepted articles to
                                Render suspension between 2026-03-28 and 2026-04-15.
                                IT ALWAYS ATTEMPTS, IT DOES NOT ALWAYS MINT: the evaluation passes
                                gates (agent-enabled, staleness, frequency cap, rejection pattern) and
                                can produce nothing, with no signal on this surface. So the label is
                                accurate as "this is what starts the minting" and would overstate if
                                read as "an idea is guaranteed". */}
                            <Button
                                size="sm"
                                disabled={busy}
                                className={`min-h-[44px] gap-1.5 ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} text-base font-semibold shadow-sm disabled:opacity-60`}
                                onClick={() => handleAction('save')}
                            >
                                {busy ? (
                                    <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Saving...</>
                                ) : (
                                    <><BookmarkSimple weight="bold" className="w-4 h-4" /> {PRIMARY_SAVE_LABEL}</>
                                )}
                            </Button>

                            {/* Override: Reject with reason (opposite of recommendation) */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    {/* DECISION: Override to Reject maps to 'negative' intent */}
                                    <Button
                                        size="sm"
                                        disabled={busy}
                                        className={`min-h-[44px] gap-1.5 ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} text-base font-semibold disabled:opacity-60`}
                                    >
                                        <ArrowUUpLeft weight="bold" className="w-4 h-4" />
                                        {OVERRIDE_LABEL_WHEN_RECOMMENDED_SAVE}
                                        <CaretDown weight="bold" className="w-3 h-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="z-[100] bg-white border border-slate-200 shadow-lg rounded-md w-72">
                                    {rejectionReasons.map((reason, i) => (
                                        <DropdownMenuItem
                                            key={i}
                                            className="text-base py-3 cursor-pointer"
                                            onSelect={() => handleAction('reject', reason)}
                                        >
                                            {reason}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <>
                            {/* Direct Action: Confirm dismiss — one click (agrees with agent) */}
                            {/* DECISION: Confirm Dismiss maps to 'negative' intent */}
                            <Button
                                size="sm"
                                disabled={busy}
                                className={`min-h-[44px] gap-1.5 ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} text-base font-semibold shadow-sm disabled:opacity-60`}
                                onClick={() => handleAction('reject', 'Agreed with agent recommendation')}
                            >
                                {busy ? (
                                    <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Dismissing...</>
                                ) : (
                                    <><X weight="bold" className="w-4 h-4" /> Confirm Dismiss</>
                                )}
                            </Button>

                            {/* Override: Save with reason (opposite of recommendation) */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    {/* DECISION: Override to Save maps to 'primary' intent */}
                                    <Button
                                        size="sm"
                                        disabled={busy}
                                        className={`min-h-[44px] gap-1.5 ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} text-base font-semibold disabled:opacity-60`}
                                    >
                                        <ArrowUUpLeft weight="bold" className="w-4 h-4" />
                                        {OVERRIDE_LABEL_WHEN_RECOMMENDED_DISCARD}
                                        <CaretDown weight="bold" className="w-3 h-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="z-[100] bg-white border border-slate-200 shadow-lg rounded-md w-72">
                                    {overrideReasons.map((reason, i) => (
                                        <DropdownMenuItem
                                            key={i}
                                            className="text-base py-3 cursor-pointer"
                                            onSelect={() => handleAction('override', reason)}
                                        >
                                            {reason}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
